/**
 * Display, inbox-bucket, and badge config for notifications.
 */
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import type { NotificationType } from '@/types/notification.types';

export { NOTIFICATION_QUERY_KEYS } from '@/constants/shared-catalog';

/** Icon + color tokens for a notification type. */
export interface NotificationTypeConfig {
  icon: LucideIcon;
  colorClass: string;
  bgClass: string;
}

/** Fallback icon when the type is missing from {@link NOTIFICATION_TYPE_CONFIG}. */
export const NOTIFICATION_TYPE_FALLBACK: NotificationTypeConfig = {
  icon: FileText,
  colorClass: 'notif-icon-slate',
  bgClass: 'notif-icon-bg-slate',
};

/** Icon + colors for each inbox notification type. */
export const NOTIFICATION_TYPE_CONFIG: Record<NotificationType, NotificationTypeConfig> = {
  SESSION_REPORT_REMINDER_24H: {
    icon: Clock,
    colorClass: 'notif-icon-amber',
    bgClass: 'notif-icon-bg-amber',
  },
  SESSION_REPORT_REMINDER_96H: {
    icon: AlertCircle,
    colorClass: 'notif-icon-red',
    bgClass: 'notif-icon-bg-red',
  },
  SESSION_REPORT_LOW_RATING_COORDINATOR: {
    icon: AlertCircle,
    colorClass: 'notif-icon-red',
    bgClass: 'notif-icon-bg-red',
  },
  VSC_DAILY_REPORT_AVAILABLE: {
    icon: CalendarClock,
    colorClass: 'notif-icon-blue',
    bgClass: 'notif-icon-bg-blue',
  },
  SESSION_REPORT_VALIDATED_TUTOR: {
    icon: CheckCircle2,
    colorClass: 'notif-icon-green',
    bgClass: 'notif-icon-bg-green',
  },
  BINOME_CANCELLATIONS_THRESHOLD_COORDINATOR: {
    icon: AlertCircle,
    colorClass: 'notif-icon-amber',
    bgClass: 'notif-icon-bg-amber',
  },
  SESSION_RESCHEDULED_BY_TUTOR: {
    icon: CalendarClock,
    colorClass: 'notif-icon-blue',
    bgClass: 'notif-icon-bg-blue',
  },
};

/** Inbox grouping: today, last 7 days, or older. */
export type NotificationBucket = 'today' | 'week' | 'older';

/** Section titles for {@link NotificationBucket} (UI copy). */
export const NOTIFICATION_BUCKET_LABELS: Record<NotificationBucket, string> = {
  today: "Aujourd'hui",
  week: 'Cette semaine',
  older: 'Plus ancien',
};

/** Display order of inbox groups (empty groups are omitted by the utils). */
export const NOTIFICATION_BUCKET_ORDER: NotificationBucket[] = ['today', 'week', 'older'];

/** Empty-inbox title and subtitle. */
export const NOTIFICATION_EMPTY_COPY = {
  title: 'Aucune notification',
  subtitle: 'Vous êtes à jour. Les nouvelles notifications apparaîtront ici.',
};

/** Pixels from the list bottom that trigger the next page. */
export const NOTIFICATION_SCROLL_LOAD_THRESHOLD_PX = 80;

/** Placeholder rows while the inbox query is loading. */
export const NOTIFICATION_SKELETON_ROWS = 3;

/** Unread badge shows `9+` once the count exceeds this value. */
export const NOTIFICATION_BADGE_CAP = 9;

/** Page size for the infinite inbox query. */
export const NOTIFICATION_LIST_PAGE_SIZE = 25;
